import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import wordmark from '../assets/innovarxi-wordmark.svg';
import UserIcon from './icons/UserIcon';
import SignoutIcon from './icons/SignoutIcon';
import GearSixIcon from './icons/GearSixIcon';
import FrameIcon from './icons/FrameIcon';
import PackageIcon from './icons/PackageIcon';
import Frame1Icon from './icons/Frame1Icon';
import ListIcon from './icons/ListIcon';
import ShippingIcon from './icons/ShippingIcon';
import useMenuItems from '../hooks/reactQuery/useMenuItems';
import pagesSlug from '../constant/pages-slug';
import useProfile from '../hooks/useProfile';

interface Aggregates {
  [key: string]: number;
}

type IProps = {
  showSidebar: boolean;
  selectedMenuItem: string;
  onMenuItemClick: any;
  aggregates: Aggregates;
};

type CountTone = 'sky' | 'warning' | 'navy' | 'blue' | 'violet';

type MenuItem = {
  icon: React.ReactNode;
  label: string;
  href: string;
  slug: string;
  key: string;
  section: string;
  countTone?: CountTone;
};

const menuItems: MenuItem[] = [
  {
    icon: <FrameIcon />,
    label: 'Search',
    href: '/',
    slug: pagesSlug.dashboard,
    key: 'Orders',
    section: 'Orders',
  },
  {
    icon: <PackageIcon />,
    label: 'New Orders',
    href: '/new-orders',
    slug: pagesSlug.newOrders,
    key: 'Orders',
    section: 'Orders',
    countTone: 'sky',
  },
  {
    icon: <Frame1Icon />,
    label: 'Product Kitting',
    href: '/kitting-orders',
    slug: pagesSlug.kitting,
    key: 'Orders',
    section: 'Orders',
    countTone: 'warning',
  },
  {
    icon: <ShippingIcon />,
    label: 'Order in Shipping',
    href: '/shipping-orders',
    slug: pagesSlug.shipping,
    key: 'Orders',
    section: 'Orders',
    countTone: 'navy',
  },
  {
    icon: <ListIcon />,
    label: 'Order in Delivery',
    href: '/delivery-orders',
    slug: pagesSlug.delivered,
    key: 'Orders',
    section: 'Orders',
    countTone: 'blue',
  },
  {
    icon: <ListIcon />,
    label: 'Thunderbird',
    href: '/thunderbird-orders',
    slug: pagesSlug.thunderbird,
    key: 'Orders',
    section: 'Orders',
    countTone: 'violet',
  },
  // Assistant pages: api/menu grants AskAI and Schedules to admins only.
  {
    icon: <FontAwesomeIcon icon={faWandMagicSparkles} />,
    label: 'Ask AI',
    href: '/ask-ai',
    slug: pagesSlug.askAi,
    key: 'AskAI',
    section: 'Assistant',
  },
  {
    icon: <FontAwesomeIcon icon={faCalendarCheck} />,
    label: 'Schedules',
    href: '/schedules',
    slug: pagesSlug.schedules,
    key: 'Schedules',
    section: 'Assistant',
  },
  {
    icon: <UserIcon />,
    label: 'Users',
    href: '/users',
    slug: pagesSlug.users,
    key: 'Users',
    section: 'Admin',
  },
  {
    icon: <GearSixIcon />,
    label: 'Settings',
    href: '/settings',
    slug: pagesSlug.settings,
    key: 'Settings',
    section: 'Admin',
  },
];

const formatCount = (count: number) => (count > 999 ? '999+' : String(count));

const Sidebar = ({
  showSidebar = false,
  selectedMenuItem,
  onMenuItemClick,
  aggregates,
}: IProps) => {
  const { data: allMenuItems } = useMenuItems();
  const profile = useProfile();

  const sections = useMemo(() => {
    const allowed: string[] = allMenuItems?.pages || [];
    if (!allowed.length) return [];
    const visible = menuItems.filter((item) => allowed.includes(item.key));
    const names = Array.from(new Set(visible.map((item) => item.section)));
    return names.map((name) => ({
      name,
      items: visible.filter((item) => item.section === name),
    }));
  }, [allMenuItems?.pages]);

  const displayName = profile.name;
  const displayMeta = profile.role || profile.email;

  return (
    <aside
      className={`sidebar ${
        showSidebar ? 'open-sidebar' : 'collapsed-sidebar'
      }`}
    >
      <div className="sidebar-logo">
        <img
          src={wordmark}
          alt="innovaRXI"
          className="logo"
          width={196}
          height={43}
        />
      </div>
      <nav className="sidebar-nav">
        {sections.map((section) => (
          <div key={section.name}>
            <div className="sidebar-section">{section.name}</div>
            <ul className="sidebar-list">
              {section.items.map((item) => {
                const count = aggregates?.[item.slug] || 0;
                return (
                  <li key={item.slug}>
                    <NavLink
                      to={item.href}
                      className={`sidebar-link ${
                        selectedMenuItem === item.slug ? 'is-active' : ''
                      }`}
                      // The count badge is part of the link text; the label keeps telemetry
                      // (and screen readers) from reading "New Orders132".
                      aria-label={item.label}
                      onClick={() => onMenuItemClick(item.slug)}
                    >
                      {item.icon}
                      <span className="sidebar-label">{item.label}</span>
                      {item.countTone && count > 0 ? (
                        <span
                          className={`sidebar-count sidebar-count--${item.countTone}`}
                          title={String(count)}
                        >
                          {formatCount(count)}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="sidebar-user">
        <span className="sidebar-avatar" aria-hidden="true">
          {profile.initials}
        </span>
        <div className="sidebar-user-text">
          <div className="sidebar-user-name" title={displayName}>
            {displayName}
          </div>
          <div className="sidebar-user-meta" title={profile.email}>
            {displayMeta}
          </div>
        </div>
        <button
          type="button"
          className="sidebar-signout"
          title="Sign out"
          aria-label="Sign out"
          onClick={() => onMenuItemClick(pagesSlug.logout)}
        >
          <SignoutIcon />
        </button>
      </div>
    </aside>
  );
};

export default React.memo(Sidebar);
